import { ProgressBar } from '@pwc/pwc-deals-ui';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import {
  SURVEY_DIRECTION,
  validate,
} from '../../common/services/utilities/surveyUtility';
import formatAnswer from './utility/formatAnswer';
import compareNewAnswerToExistingAnswer from './utility/compareNewAnswerToExistingAnswer';
import { formatNextRoute, formatPreviousRoute } from '../../routes';
import {
  getFetchQuestionStatus,
  postAnswer,
  putAnswer,
  resetFetchQuestionStatus,
  resetFetchSurveyResponsesStatus,
  resetSubmitAnswerStatus,
  getSubmitAnswerStatus,
  getSurveyResponses,
} from '../../surveySlice';

import QuestionSectionHeader from './QuestionSectionHeader/QuestionSectionHeader';

import './Question.scss';
import QuestionType from './QuestionType/QuestionType';
import QuestionFooter from './QuestionFooter/QuestionFooter';

const proceedPath = (direction, projectId) =>
  direction === SURVEY_DIRECTION.PREVIOUS
    ? formatPreviousRoute(projectId)
    : formatNextRoute(projectId);
const navigateToNextQuestion = (direction, history, projectId) =>
  history.push(proceedPath(direction, projectId));

const Question = ({ question }) => {
  const [userAnswer, setUserAnswer] = useState(null);
  const [isAnswerValid, setIsAnswerValid] = useState();
  const [totalSteps, setTotalSteps] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [surveyDirection, setSurveyDirection] = useState(SURVEY_DIRECTION.NEXT);
  const [isConditionalQuestion, setIsConditionalQuestion] = useState(false);

  const history = useHistory();

  const { projectId } = useParams();
  const dispatch = useDispatch();

  const { pending: fetchQuestionPending } = useSelector(getFetchQuestionStatus);
  const { success: submitAnswerSuccess, pending: submitAnswerPending } =
    useSelector(getSubmitAnswerStatus);
  const surveyResponses = useSelector(getSurveyResponses);

  const isNextDisabled = submitAnswerPending || !isAnswerValid;
  const isFirstQuestion = question && question.order === 1;

  useEffect(() => {
    dispatch(resetFetchQuestionStatus());
    dispatch(resetFetchSurveyResponsesStatus());
  }, [dispatch]);

  useEffect(() => {
    if (surveyResponses) {
      setTotalSteps(
        surveyResponses.sectionCompletionStatus.reduce(
          (prev, cur) => prev + cur.numQuestionsTotal,
          0
        )
      );
      setTotalAnswered(
        surveyResponses.sectionCompletionStatus.reduce(
          (prev, cur) => prev + cur.numQuestionsAnswered,
          0
        )
      );

      setIsConditionalQuestion(question && question.conditions);
    }
  }, [surveyResponses, question]);

  const showProgressBar = totalAnswered > 0;

  useEffect(() => {
    if (submitAnswerSuccess) {
      dispatch(resetSubmitAnswerStatus());
      navigateToNextQuestion(surveyDirection, history, projectId);
    }
  }, [submitAnswerSuccess, history, projectId, surveyDirection, dispatch]);

  const getAnswerId = (answer) => {
    const foundAnswer = surveyResponses.answers.find(
      (ans) => ans.questionId === answer.questionId
    );
    if (foundAnswer) {
      return foundAnswer.id;
    }
    return null;
  };

  const submitAnswer = (answer) => {
    if (answer.length) {
      dispatch(postAnswer(projectId, answer));
    } else {
      const answerId = getAnswerId(answer);
      if (answerId) {
        dispatch(putAnswer(projectId, { ...answer, id: answerId }));
      } else {
        dispatch(postAnswer(projectId, answer));
      }
    }
  };

  const validateAnswer = (answer) => {
    if (question.type === 'radio') {
      return validate.questionRadio(answer);
    }
    if (question.type === 'percent') {
      return validate.questionPercent(answer);
    }
    if (question.type === 'multiselect') {
      return validate.questionMultiselect(answer);
    }
    return null;
  };

  const onProceed = (direction) => {
    setSurveyDirection(direction);

    const { isValid } = validateAnswer(userAnswer);
    if (isValid) {
      const formattedAnswer = formatAnswer(projectId, question, userAnswer);
      const doesAnswerMatch = compareNewAnswerToExistingAnswer(
        formattedAnswer,
        surveyResponses.answers
      );
      if (!doesAnswerMatch) {
        return submitAnswer(formattedAnswer);
      }
    }
    return navigateToNextQuestion(direction, history, projectId);
  };

  const onPrevious = () => onProceed(SURVEY_DIRECTION.PREVIOUS);

  const onNext = () => onProceed(SURVEY_DIRECTION.NEXT);

  const skipPath = proceedPath(SURVEY_DIRECTION.NEXT, projectId);

  return (
    !fetchQuestionPending && (
      <>
        {showProgressBar && (
          <>
            <ProgressBar total={totalSteps} current={totalAnswered} />
            <hr />
          </>
        )}
        <div className="diagnostic-content">
          <QuestionSectionHeader sectionId={question.sectionId} />
          <div
            className={`dd-question-wrapper card-container ${
              isConditionalQuestion && 'conditional-question'
            }`}
          >
            <QuestionType
              question={question}
              userAnswer={userAnswer}
              setUserAnswer={setUserAnswer}
              setIsAnswerValid={setIsAnswerValid}
            />
            <QuestionFooter
              onNext={onNext}
              onPrevious={onPrevious}
              skipPath={skipPath}
              isNextDisabled={isNextDisabled}
              isBackDisabled={isFirstQuestion}
            />
          </div>
        </div>
      </>
    )
  );
};

export default Question;
